import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import useFocusOnMount from '../hooks/useFocusOnMount';
import styles from './AuthPages.module.css';

export default function SignupPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { signup, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const mainRef = useFocusOnMount();

  async function handleSubmit(e) {
    e.preventDefault();
    clearError();
    try {
      const result = await signup({ email, password });
      if (result?.email_verification_pending) {
        setSentEmail(email);
        setEmailSent(true);
      } else {
        // Route through the app root so OnboardingGuard decides the next step
        // (the language gate fires here for users who haven't chosen one).
        navigate('/');
      }
    } catch {
      // Error is set in store
    }
  }

  async function handleResend() {
    setResendLoading(true);
    setResendMessage('');
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sentEmail }),
      });
      setResendMessage('Sent! Check your inbox (and spam folder).');
    } catch {
      setResendMessage('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <main className={styles.page} ref={mainRef} tabIndex={-1}>
      {/* Left panel — illustration + logo */}
      <div className={styles.authLeft}>
        <img
          src="/assets/auth-illustration.webp"
          alt=""
          className={styles.bgImage}
        />
        <img
          src="/assets/wanderwords-logo.svg"
          alt="WanderWords"
          className={styles.logo}
        />
      </div>

      {/* Right panel — form + footer */}
      <div className={styles.authRight}>
        <div className={styles.content}>
        {emailSent ? (
          <div className={styles.verificationPending}>
            <p className={styles.verificationTitle}>Check your inbox</p>
            <p className={styles.verificationBody}>
              We sent a confirmation link to <strong>{sentEmail}</strong>. Click the link to activate your account.
            </p>
            <p className={styles.verificationNote}>
              It may take a minute. Check your spam folder if you don't see it.
            </p>
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className={styles.resendBtn}
            >
              {resendLoading ? 'Sending\u2026' : 'Resend confirmation email'}
            </button>
            {resendMessage && (
              <p className={styles.resendMessage}>{resendMessage}</p>
            )}
          </div>
        ) : (
          <>
            {error && (
              <div className={styles.errorBanner} role="alert">{error}</div>
            )}

            <h1 className={styles.cardTitle}>{t('auth.createAccount')}</h1>

            <form id="signup-form" onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label htmlFor="signup-email" className={styles.label}>{t('auth.email')}</label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="signup-password" className={styles.label}>{t('auth.password')}</label>
                <input
                  id="signup-password"
                  type="password"
                  placeholder={t('auth.passwordHint')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className={styles.input}
                />
              </div>
            </form>

            <div className={styles.actions}>
              {/* ToS acceptance */}
              <div className={styles.tosRow}>
                <input
                  id="tos-checkbox"
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className={styles.tosCheckbox}
                />
                <label htmlFor="tos-checkbox" className={styles.tosLabel}>
                  By creating an account I agree to the{' '}
                  <a
                    href="https://wanderwords.co/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.tosLink}
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="https://wanderwords.co/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.tosLink}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="submit"
                form="signup-form"
                disabled={isLoading || !tosAccepted}
                className={styles.submitBtn}
              >
                {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </button>

              <p className={styles.switchPrompt}>
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className={styles.switchLink}>
                  {t('auth.logIn')}
                </Link>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Page footer */}
      <footer className={styles.pageFooter}>
        <a
          href="https://wanderwords.co/terms"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerLink}
        >
          Terms of Service
        </a>
        <a
          href="https://wanderwords.co/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerLink}
        >
          Privacy Policy
        </a>
        <p className={styles.footerCopyright}>&copy; 2026 Bricksquare Pte. Ltd.</p>
        </footer>
      </div>
    </main>
  );
}
